import { useEffect, useMemo, useState } from "react";
import brew from "brew-js";
import { getRect, scrollIntoView } from "zeta-dom/domUtil";
import { ChoiceFieldProps, classNames, useFormField } from "zeta-dom-react";
import { Mixin, useFlyoutMixin, useScrollableMixin } from "brew-js-react";
import { IconNames } from "src/components/common";
import { Button, validators } from ".";
import { combineFn } from "zeta-dom/util";
import { TextField } from "./TextField";

export interface DropdownItem<T = string> {
    value: T;
    label: string;
    description?: string;
}

export interface DropdownProps<T = string> extends ChoiceFieldProps<DropdownItem<T>> {
    label?: string;
    unselectedText?: string;
    icon?: IconNames;
    required?: boolean;
    allowUnselect?: boolean;
    showSearchBox?: boolean;
    searchBoxPlaceholder?: string;
    showErrorMessage?: boolean;
}

export function Dropdown<T = string>(props: DropdownProps<T>) {
    props = {
        ...props,
        onValidate: validators.combine<T | ''>(
            props.required && validators.required(),
            props.onValidate
        )
    };
    const flyout = useFlyoutMixin();
    const scrollable = useScrollableMixin({ direction: 'y-only' });
    const [opened, isOpened] = useState(false);
    const [active, isActive] = useState(false);
    const [search, setSearch] = useState('');
    const { value, error, setValue, elementRef, items, selectedItem } = useFormField('choice', props, '');


    const displayItems = useMemo(() => {
        let matchText = search.toLowerCase();
        return matchText ? items.filter(v => v.label.toLowerCase().includes(matchText) || v.description?.toLowerCase().includes(matchText)) : items;
    }, [items, search]);

    const label = selectedItem?.label || props.label || props.unselectedText || 'Select';

    useEffect(() => {
        return combineFn(
            flyout.onToggleState(isActive),
            flyout.onVisibilityChanged(opened1 => {
                isOpened(opened1);
                if (opened1) {
                    let container = scrollable.elements()[0];
                    let activeElement = container.querySelector('.active');
                    if (activeElement) {
                        scrollable.scrollToElement(activeElement, 'center', 'center');
                    }
                    let delta = scrollIntoView(container) || { x: 0, y: 0 };
                    container.style.maxHeight = (getRect().bottom - getRect(container).top - delta.y - 10) + 'px';
                }
            })
        );
    }, [flyout, scrollable]);

    return (
        <div ref={elementRef} className={classNames('eac-field eac-dropdown', { opened, active, disabled: props.disabled, 'has-label': !!(selectedItem && props.label), 'has-error': !!error })}>
            {props.label &&
                <div className="eac-dropdown-label">{props.label}</div>}
            <Button {...Mixin.use(flyout.toggle)} icon={props.icon} label={label} disabled={props.disabled} />
            <div {...Mixin.use(flyout.withEffects('fade-in'), classNames('eac-dropdown-flyout'))}>
                <Button {...Mixin.use(flyout.toggle)} icon={props.icon} label={label} />
                {props.showSearchBox &&
                    <TextField icon="search" placeholder={props.searchBoxPlaceholder} value={search} onChange={setSearch} />}
                <div {...Mixin.use(scrollable, classNames('eac-dropdown-flyout-container'))}>
                    <div {...Mixin.use(scrollable.target)}>
                        {displayItems.map((v, i) => (
                            <button key={i} type="button" className={classNames('eac-button', { active: v.value === value })} onClick={() => onClick(v.value)}>
                                <span>{v.label}</span>
                                {v.description &&
                                    <span className="eac-button-description">{v.description}</span>}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {error && props.showErrorMessage !== false &&
                <div className="eac-field-error">{error}</div>}
        </div>
    );

    function onClick(value1: T) {
        setValue(value1);
        brew.closeFlyout(flyout.elements()[0]);
    }
}
